/**
 * screens/_layout.tsx — the shared screen frame every screen sits in:
 * a scrolling page with a centered 1080-max column and the standard header
 * (h1 + muted subtitle + right-aligned actions). Keeps page chrome identical
 * across all five screens.
 */
import React from 'react';
import { ScrollView, View } from 'react-native';
import { LightColors as C, FontSize as FS, FontWeight as FW, Shadows as SH } from '../theme/theme';
import { useResponsive } from '../lib/responsive';
import { CONTENT } from '../lib/responsive';
import { Txt, Row } from '../components/ui';
import Icon, { IconName } from '../components/icon';

export const Page: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { gutter } = useResponsive();
  return (
    <ScrollView style={{ flex: 1, backgroundColor: C.paper }} contentContainerStyle={{ paddingBottom: 80 }}>
      <View
        style={{
          width: '100%',
          maxWidth: CONTENT.default,
          alignSelf: 'center',
          paddingHorizontal: gutter,
          paddingTop: 28,
        }}
      >
        {children}
      </View>
    </ScrollView>
  );
};

export const PageHeader: React.FC<{
  title: string;
  subtitle?: React.ReactNode;
  /** Premium icon-badge + letterspaced label above the title. */
  eyebrow?: string;
  icon?: IconName;
  actions?: React.ReactNode;
  style?: object;
}> = ({ title, subtitle, eyebrow, icon, actions, style }) => (
  <View
    style={[
      { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
      style,
    ]}
  >
    <View style={{ flexShrink: 1, minWidth: 0 }}>
      {icon || eyebrow ? (
        <Row gap={9} style={{ alignItems: 'center', marginBottom: 12 }}>
          {icon ? (
            <View
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                backgroundColor: C.ink,
                alignItems: 'center',
                justifyContent: 'center',
                ...(SH.sm as object),
              }}
            >
              <Icon name={icon} size={16} color="#FFFFFF" strokeWidth={2.2} />
            </View>
          ) : null}
          {eyebrow ? (
            <Txt size={10.5} weight={FW.bold} color={C.ink4} style={{ letterSpacing: 1.6, textTransform: 'uppercase' }}>
              {eyebrow}
            </Txt>
          ) : null}
        </Row>
      ) : null}
      <Txt size={FS['3xl']} weight={FW.bold} color={C.ink} tracking={-0.7}>
        {title}
      </Txt>
      {subtitle ? (
        <Txt size={FS.lg} color={C.ink3} style={{ marginTop: 8, maxWidth: 720 }} lineHeight={25}>
          {subtitle}
        </Txt>
      ) : null}
    </View>
    {actions ? <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>{actions}</View> : null}
  </View>
);
